import React, { createContext, useContext, useReducer } from "react";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import { useSignupQueryParams } from "../hooks/useSignupQueryParams";
import { useSignupTitle } from "../hooks/useSignupTitle";
import { useSnapshot } from "../hooks/useSnapshot";
import { ISelfsignupContext, ESignupStages, ISelfsignupProviderProps, ISelfsignupState, OnboardingSources } from "../types";
import { goBack, setHubSpotId, setLeadId, setStage, setSubscription, setReseller } from "./signup.action";
import { SignupReducer } from "./signup.state";
import { useSentry } from "../../../hooks/useSentry";
import { useSnackbarMessages } from "../../../yoello-lib/modules/components/snackbar/SnackbarContext";
import { useTranslation } from "react-i18next";
import hostClient from "../../../yoello-lib/modules/host/HostClient";


export const SelfsignupContext = createContext<ISelfsignupContext | undefined>(undefined);

/**Set hook and force it to not be undefined */
export const useSignup = () => useContext(SelfsignupContext) as ISelfsignupContext;

const hostName = hostClient.host;

const initalState: ISelfsignupState = {
    stage: ESignupStages.DETAILS,
    title: "",
    selectedSubscription: null,
    host: hostName
}

const LEADS = 'leads/';
const SIGNUP = 'signup/';
const LOOKUP = 'lookup';
const FEEPLAN = 'fee_plans';

export function SelfSignupProvider({
    children
}: ISelfsignupProviderProps) {
    const { callAPI } = useAPIV2<any>(APIExtensions.registration, { onMountDisabled: true });

    const [state, dispatch] = useReducer(SignupReducer, { ...initalState, host: hostName });

    const { stage, leadId, host, hubspotId } = state;

    const { t } = useTranslation();
    const { sendSnackbarMessage } = useSnackbarMessages()!;
    const { removeSnapshot } = useSnapshot(dispatch, "sign-up", state, snapshotCheck,
        stage === ESignupStages.COMPLETED || stage === ESignupStages.INVALIDLOCATION);

    const { reseller } = useSignupQueryParams(dispatch);

    useSignupTitle(dispatch, stage, host);
    
    const { captureException } = useSentry();

    async function callLeadsAPI<T>(body: T) {
        const method = leadId ? HTTPMethods.PATCH : HTTPMethods.POST;
        const messageOptions = leadId ? { query: `${LEADS}${leadId}` } : { query: LEADS }
        const { isSuccessful, data } = await callAPI(method, {
            ...messageOptions, body: {
                ...body,
                onboarding_source: OnboardingSources[host],
                hubspot_id: hubspotId ?? undefined,
                reseller: reseller
            }
        });
        if (isSuccessful) {
            if (!leadId) {
                dispatch(setLeadId(data.lead_id as string));
                dispatch(setHubSpotId(data.hubspot_id));
                dispatch(setReseller(data.reseller));
            }
        } else {
            captureException(data, `${method}-leadsAPI`, body);
        }
    }

    async function emailLookup(email: string) {
        const { isSuccessful, data } = await callAPI(HTTPMethods.GET, {
            query: LOOKUP, params: { contact_email: email.toLowerCase() }, headers: { 'Authorization': undefined }
        });
        if (isSuccessful) {
            if (data?.email_exists !== undefined) {
                return data?.email_exists;
            }
        }
        return true;
    }

    async function snapshotCheck(snapshot: ISelfsignupState) {
        if (snapshot?.userData?.contact_email) {
            const isEmail = await emailLookup(snapshot?.userData?.contact_email);
            if (isEmail) {
                sendSnackbarMessage(t('signup.form.emailError'), 'error');
                if (host === "partners") {
                    dispatch(setStage(ESignupStages.DETAILS));
                }
            }
        }
    }

    async function completeSignup() {
        if (!leadId) {
            console.log("NO lead id");
            return false;
        }
        const { isSuccessful, data } = await callAPI(HTTPMethods.POST, { query: SIGNUP, params: { lead_id: state.leadId }, body: {} });

        if (isSuccessful) {
            console.log("Completed signup");
        } else {
            captureException(data, `complete-signup`, state);
        }
        return isSuccessful;
    }

    async function getTransactionFeePlans(resellerName: string) {
        const { isSuccessful, data } = await callAPI(HTTPMethods.GET, {
         query:FEEPLAN,
         params: { reseller_name: resellerName }
        });
        if (isSuccessful) {
            return data;
        } else return [];
    }

    function goBackwards() {
        dispatch(goBack())
    }

    function selectSubscription(id: string | null) {
        dispatch(setSubscription(id));
    }

    return <SelfsignupContext.Provider value={{
        ...state,
        dispatch,
        goBack: goBackwards,
        selectSubscription,
        removeSnapshot,
        callLeadsAPI,
        completeSignup,
        emailLookup,
        getTransactionFeePlans
    }}>
        {children}
    </SelfsignupContext.Provider>
}